import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, ArrowRight, UserPlus, Trophy, ClockCounterClockwise, Sparkle, Coins, Camera, Key, PencilSimple, MagnifyingGlass, X, EnvelopeSimple, Pause, IdentificationCard } from "@phosphor-icons/react";
import GrantPackageDialog from "@/components/app/GrantPackageDialog";
import ResetPasswordButton from "@/components/app/ResetPasswordButton";
import EditUserDialog from "@/components/app/EditUserDialog";
import { toast } from "sonner";

function CreateUserDialog({ onCreated, allowRoles, coaches }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: allowRoles[0], coach_id: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please pick a role (coach or athlete)");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        coach_id: form.coach_id && form.coach_id !== "__none__" ? form.coach_id : null,
      };
      const { data } = await api.post("/users", payload);
      toast.success(`${data.role} created`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: allowRoles[0], coach_id: "" });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-terracotta hover:bg-terracotta-hover text-white hover:text-white rounded-xl" data-testid="open-create-user-dialog">
          <UserPlus size={16} className="mr-2" /> New person
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Add person</DialogTitle>
          <DialogDescription>Create a new coach or athlete account. They can log in with the email and temporary password below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="create-user-form">
          <div>
            <Label className="hs-label">Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="create-user-name" />
          </div>
          <div>
            <Label className="hs-label">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="create-user-email" />
          </div>
          <div>
            <Label className="hs-label">Temporary password</Label>
            <Input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="create-user-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">Role</Label>
              <Select value={form.role || allowRoles[0]} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="create-user-role">
                  <SelectValue placeholder="Pick role" />
                </SelectTrigger>
                <SelectContent>
                  {allowRoles.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.role === "athlete" && coaches && coaches.length > 0 && (
              <div>
                <Label className="hs-label">Assigned coach</Label>
                <Select value={form.coach_id} onValueChange={(v) => setForm({ ...form, coach_id: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="create-user-coach">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {coaches.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              className="bg-moss hover:bg-moss-hover text-white hover:text-white rounded-xl"
              data-testid="submit-create-user"
              onClick={(e) => {
                // allow form submit to proceed normally
              }}
            >
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function People() {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [owedByAthlete, setOwedByAthlete] = useState({});
  // Roster search — matches name, email, OR assigned coach's name.
  const [query, setQuery] = useState("");

  const load = async () => {
    const [aRes, cRes, billing] = await Promise.all([
      api.get("/users", { params: { role: "athlete" } }),
      // Coaches load the coach list too — needed to group athletes under
      // their assigned coach on the roster, even though only admin can
      // create/edit coach accounts.
      api.get("/users", { params: { role: "coach" } }),
      api.get("/admin/billing-summary").catch(() => ({ data: {} })),
    ]);
    setAthletes(aRes.data);
    setCoaches(cRes.data);
    setOwedByAthlete(billing.data || {});
  };

  useEffect(() => { load(); }, []);

  const reassignCoach = async (athleteId, newCoachId) => {
    try {
      await api.patch(`/users/${athleteId}`, { coach_id: newCoachId || null });
      toast.success(newCoachId
        ? `Reassigned to ${coaches.find((c) => c.id === newCoachId)?.name || "coach"}`
        : "Marked unassigned");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (id, kind) => {
    if (!window.confirm(`Delete this ${kind}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const allowRoles = user.role === "admin" ? ["coach", "athlete"] : ["athlete"];

  const handleCreated = (u) => {
    if (u.role === "coach") setCoaches((c) => [...c, u]);
    else setAthletes((a) => [...a, u]);
  };

  // Apply the roster search to both tabs. Empty query → unchanged lists.
  const q = query.trim().toLowerCase();
  const matchPerson = (p) => {
    if (!q) return true;
    if (p.name && p.name.toLowerCase().includes(q)) return true;
    if (p.email && p.email.toLowerCase().includes(q)) return true;
    if (p.coach_id) {
      const c = coaches.find((cc) => cc.id === p.coach_id);
      if (c && c.name.toLowerCase().includes(q)) return true;
    }
    return false;
  };
  const filteredAthletes = athletes.filter(matchPerson);
  const filteredCoaches = coaches.filter(matchPerson);

  const activeAthletes = filteredAthletes.filter((a) => a.is_active !== false);
  const inactiveAthletes = filteredAthletes.filter((a) => a.is_active === false);

  // Group filtered athletes by their assigned coach. Coaches are listed in
  // their existing sort order (server returns name-sorted); unassigned
  // athletes get a trailing "Unassigned" bucket so they're never lost.
  const athleteGroups = useMemo(() => {
    const groups = coaches
      .map((c) => ({
        coachId: c.id,
        coachName: c.name,
        athletes: activeAthletes.filter((a) => a.coach_id === c.id),
      }))
      .filter((g) => g.athletes.length > 0);
    const unassigned = activeAthletes.filter((a) => !a.coach_id);
    if (unassigned.length > 0) {
      groups.push({ coachId: null, coachName: "Unassigned", athletes: unassigned });
    }
    return groups;
  }, [coaches, activeAthletes]);

  const toggleActive = async (athlete) => {
    const nextActive = athlete.is_active === false;
    try {
      await api.patch(`/users/${athlete.id}`, { is_active: nextActive });
      toast.success(nextActive
        ? `${athlete.name} marked active`
        : `${athlete.name} moved to Inactive`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="hs-label">People</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">Coaches & athletes</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user.role === "admin" && <WeeklyRecapButton athleteCount={athletes.length} />}
          <CreateUserDialog onCreated={handleCreated} allowRoles={allowRoles} coaches={coaches} />
        </div>
      </div>

      <Tabs defaultValue="athletes" className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <TabsList className="bg-muted2 rounded-xl p-1 self-start">
            <TabsTrigger value="athletes" className="rounded-lg data-[state=active]:bg-surface data-[state=active]:text-ink" data-testid="tab-athletes">
              Athletes ({activeAthletes.length}{q ? ` of ${athletes.filter((a) => a.is_active !== false).length}` : ""})
            </TabsTrigger>
            <TabsTrigger value="inactive" className="rounded-lg data-[state=active]:bg-surface data-[state=active]:text-ink" data-testid="tab-inactive">
              Inactive ({inactiveAthletes.length})
            </TabsTrigger>
            {user.role === "admin" && (
              <TabsTrigger value="coaches" className="rounded-lg data-[state=active]:bg-surface data-[state=active]:text-ink" data-testid="tab-coaches">
                Coaches ({filteredCoaches.length}{q ? ` of ${coaches.length}` : ""})
              </TabsTrigger>
            )}
          </TabsList>
          {/* Roster search — filters by name, email, or assigned coach name. */}
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, or coach…"
              aria-label="Search people"
              className="h-11 rounded-xl pl-9 pr-9"
              data-testid="people-search"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-ink-3 hover:bg-muted2"
                aria-label="Clear search"
                data-testid="people-search-clear"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>

        <TabsContent value="athletes" className="mt-6">
          {/* Mobile: one section per coach, each with its own card list */}
          <div className="md:hidden space-y-6">
            {filteredAthletes.length === 0 && <div className="hs-card p-8 text-center text-ink-3">{q ? `No athletes match "${query}".` : "No athletes yet."}</div>}
            {athleteGroups.map((group) => (
              <section key={group.coachId || "unassigned"} data-testid={`mobile-coach-group-${group.coachId || "unassigned"}`}>
                <div className="hs-label text-[11px] text-ink-3 mb-2 flex items-baseline gap-2">
                  <span className="text-ink-2 font-semibold tracking-[0.18em]">{group.coachName}</span>
                  <span className="text-ink-3">·</span>
                  <span className="text-ink-3 normal-case tracking-normal">{group.athletes.length} athlete{group.athletes.length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-3">
                  {group.athletes.map((a) => {
              const coach = coaches.find((c) => c.id === a.coach_id);
              const owed = owedByAthlete[a.id];
              return (
                <div key={a.id} className="hs-card p-4" data-testid={`athlete-card-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-sage grid place-items-center text-moss font-semibold shrink-0">
                      {a.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-ink truncate">{a.name}</span>
                        {owed && owed.total_owed_cents > 0 && (
                          <Link
                            to={`/athletes/${a.id}/billing`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-warning/15 text-warning text-[10px] font-bold uppercase tracking-[0.12em]"
                            data-testid={`mobile-owed-pill-${a.id}`}
                          >
                            <Coins size={10} weight="fill" />
                            ${(owed.total_owed_cents / 100).toFixed(0)} owed
                          </Link>
                        )}
                      </div>
                      <div className="text-xs text-ink-3 truncate">{a.email}</div>
                      {coach && <div className="hs-label text-[10px] mt-1">Coach · {coach.name}</div>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <Link to={`/athletes/${a.id}/week`} className="flex-1">
                      <Button size="sm" variant="secondary" className="w-full rounded-lg h-10" data-testid={`mobile-open-week-${a.id}`}>
                        Program <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </Link>
                    <GrantPackageDialog
                      athleteId={a.id}
                      athleteName={a.name}
                      trigger={
                        <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-grant-${a.id}`} aria-label={`Grant pack to ${a.name}`} title="Grant pack">
                          <Sparkle size={16} weight="fill" className="text-terracotta" />
                        </Button>
                      }
                    />
                    <Link to={`/athletes/${a.id}/records`}>
                      <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-records-${a.id}`} aria-label={`${a.name} records`} title="Records">
                        <Trophy size={16} weight="fill" className="text-terracotta" />
                      </Button>
                    </Link>
                    <Link to={`/athletes/${a.id}/history`}>
                      <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-history-${a.id}`} aria-label={`${a.name} history`} title="History">
                        <ClockCounterClockwise size={16} className="text-ink-2" />
                      </Button>
                    </Link>
                    <Link to={`/users/${a.id}/progress`}>
                      <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-progress-${a.id}`} aria-label={`${a.name} progress photos`} title="Progress photos">
                        <Camera size={16} className="text-moss" />
                      </Button>
                    </Link>
                    <Link to={`/users/${a.id}/personal`}>
                      <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-personal-${a.id}`} aria-label={`${a.name} personal data`} title="Personal data">
                        <IdentificationCard size={16} className="text-ink-2" />
                      </Button>
                    </Link>
                    <ResetPasswordButton
                      userId={a.id}
                      userName={a.name}
                      userEmail={a.email}
                      trigger={
                        <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-reset-pwd-trigger-${a.id}`} aria-label={`Reset password for ${a.name}`} title="Reset password">
                          <Key size={16} className="text-ink-2" />
                        </Button>
                      }
                    />
                    <EditUserDialog
                      user={a}
                      onSaved={(u) => setAthletes((cur) => cur.map((x) => x.id === u.id ? u : x))}
                      trigger={
                        <Button size="sm" variant="ghost" className="rounded-lg h-10 px-3 bg-muted2 hover:bg-sage hover:text-moss" data-testid={`mobile-edit-user-trigger-${a.id}`} aria-label={`Edit info for ${a.name}`} title="Edit info">
                          <PencilSimple size={16} className="text-ink-2" />
                        </Button>
                      }
                    />
                    {user.role === "admin" && (
                      <Button size="sm" variant="ghost" onClick={() => del(a.id, "athlete")} className="rounded-lg h-10 px-2 text-error hover:bg-red-50 hover:text-error" data-testid={`mobile-delete-${a.id}`}>
                        <Trash size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
                </div>
              </section>
            ))}
          </div>

          {/* Desktop table — group separators per coach */}
          <div className="hidden md:block hs-card overflow-hidden">
            {filteredAthletes.length === 0 ? (
              <div className="p-10 text-center text-ink-3">{q ? `No athletes match "${query}".` : "No athletes yet."}</div>
            ) : (
              <table className="w-full font-data text-sm">
                <thead className="bg-muted2 text-ink-2">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">Name</th>
                    <th className="text-left px-6 py-3 font-medium">Email</th>
                    <th className="text-left px-6 py-3 font-medium">Coach</th>
                    <th className="text-right px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                {athleteGroups.map((group) => (
                  <tbody key={group.coachId || "unassigned"} data-testid={`desktop-coach-group-${group.coachId || "unassigned"}`}>
                    <tr className="bg-sage/30 border-t-2 border-moss/30">
                      <td colSpan={4} className="px-6 py-2.5">
                        <div className="hs-label text-[11px] flex items-baseline gap-2">
                          <span className="text-moss font-semibold tracking-[0.18em]">{group.coachName}</span>
                          <span className="text-ink-3">·</span>
                          <span className="text-ink-3 normal-case tracking-normal">{group.athletes.length} athlete{group.athletes.length === 1 ? "" : "s"}</span>
                        </div>
                      </td>
                    </tr>
                    {group.athletes.map((a) => {
                    const coach = coaches.find((c) => c.id === a.coach_id);
                    const owed = owedByAthlete[a.id];
                    return (
                      <tr key={a.id} className="border-t border-line" data-testid={`athlete-row-${a.id}`}>
                        <td className="px-6 py-4 font-medium text-ink">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{a.name}</span>
                            {owed && owed.total_owed_cents > 0 && (
                              <Link
                                to={`/athletes/${a.id}/billing`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-warning/15 text-warning text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-warning/25 transition-colors"
                                title={`${owed.credit_packs_count} pack(s) on credit`}
                                data-testid={`owed-pill-${a.id}`}
                              >
                                <Coins size={10} weight="fill" />
                                ${(owed.total_owed_cents / 100).toFixed(0)} owed
                              </Link>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-ink-2">{a.email}</td>
                        <td className="px-6 py-4 text-ink-2">
                          <Select
                            value={a.coach_id || "__unassigned__"}
                            onValueChange={(v) => reassignCoach(a.id, v === "__unassigned__" ? "" : v)}
                          >
                            <SelectTrigger
                              className="h-9 rounded-lg border-line bg-transparent hover:border-moss/60 font-data text-sm w-[170px]"
                              data-testid={`coach-select-${a.id}`}
                              title="Reassign this athlete to another coach"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {coaches.map((c) => (
                                <SelectItem key={c.id} value={c.id} data-testid={`coach-select-option-${c.id}-for-${a.id}`}>
                                  {c.name}
                                </SelectItem>
                              ))}
                              <SelectItem value="__unassigned__" data-testid={`coach-select-unassigned-for-${a.id}`}>
                                Unassigned
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <GrantPackageDialog
                            athleteId={a.id}
                            athleteName={a.name}
                            trigger={
                              <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`grant-pack-${a.id}`} aria-label={`Grant pack to ${a.name}`} title="Grant pack">
                                <Sparkle size={14} weight="fill" className="text-terracotta" />
                              </Button>
                            }
                          />
                          <Link to={`/athletes/${a.id}/records`}>
                            <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`open-records-${a.id}`} aria-label={`${a.name} records`} title="Records">
                              <Trophy size={14} weight="fill" className="text-terracotta" />
                            </Button>
                          </Link>
                          <Link to={`/athletes/${a.id}/history`}>
                            <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`open-history-${a.id}`} aria-label={`${a.name} history`} title="History">
                              <ClockCounterClockwise size={14} className="text-ink-2" />
                            </Button>
                          </Link>
                          <Link to={`/users/${a.id}/progress`}>
                            <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`open-progress-${a.id}`} aria-label={`${a.name} progress photos`} title="Progress photos">
                              <Camera size={14} className="text-moss" />
                            </Button>
                          </Link>
                          <Link to={`/users/${a.id}/personal`}>
                            <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`open-personal-${a.id}`} aria-label={`${a.name} personal data`} title="Personal data (sizes, etc.)">
                              <IdentificationCard size={14} className="text-ink-2" />
                            </Button>
                          </Link>
                          <ResetPasswordButton
                            userId={a.id}
                            userName={a.name}
                            userEmail={a.email}
                            trigger={
                              <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`reset-pwd-trigger-${a.id}`} aria-label={`Reset password for ${a.name}`} title="Reset password">
                                <Key size={14} className="text-ink-2" />
                              </Button>
                            }
                          />
                          <EditUserDialog
                            user={a}
                            onSaved={(u) => setAthletes((cur) => cur.map((x) => x.id === u.id ? u : x))}
                            trigger={
                              <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`edit-user-trigger-${a.id}`} aria-label={`Edit info for ${a.name}`} title="Edit info">
                                <PencilSimple size={14} className="text-ink-2" />
                              </Button>
                            }
                          />
                          <Link to={`/athletes/${a.id}/week`}>
                            <Button size="sm" variant="secondary" className="rounded-lg" data-testid={`open-week-${a.id}`}>
                              Program <ArrowRight size={14} className="ml-1" />
                            </Button>
                          </Link>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => toggleActive(a)}
                            className="rounded-lg text-ink-3 hover:bg-muted2 hover:text-ink"
                            data-testid={`mark-inactive-${a.id}`}
                            aria-label={`Mark ${a.name} inactive`}
                            title="Mark inactive"
                          >
                            <Pause size={14} />
                          </Button>
                          {user.role === "admin" && (
                            <Button size="sm" variant="ghost" onClick={() => del(a.id, "athlete")} className="rounded-lg text-error hover:bg-red-50 hover:text-error" data-testid={`delete-${a.id}`}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                ))}
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="inactive" className="mt-6" data-testid="tab-inactive-content">
          {inactiveAthletes.length === 0 ? (
            <div className="hs-card p-10 text-center text-ink-3">
              No inactive athletes. Mark one inactive from the Athletes tab and they&rsquo;ll show up here.
            </div>
          ) : (
            <div className="hs-card overflow-hidden">
              <ul className="divide-y divide-line">
                {inactiveAthletes.map((a) => {
                  const coach = coaches.find((c) => c.id === a.coach_id);
                  return (
                    <li key={a.id} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`inactive-row-${a.id}`}>
                      <div className="min-w-0 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted2 grid place-items-center text-ink-3 font-semibold shrink-0">
                          {a.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{a.name}</div>
                          <div className="text-[11px] text-ink-3 font-data">
                            {a.email}{coach ? ` · was with ${coach.name}` : ""}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => toggleActive(a)}
                        className="rounded-lg"
                        data-testid={`reactivate-${a.id}`}
                      >
                        Reactivate
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </TabsContent>

        {user.role === "admin" && (
          <TabsContent value="coaches" className="mt-6">
            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {filteredCoaches.length === 0 && <div className="hs-card p-8 text-center text-ink-3">{q ? `No coaches match "${query}".` : "No coaches yet."}</div>}
              {filteredCoaches.map((c) => (
                <div key={c.id} className="hs-card p-4" data-testid={`coach-card-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-sage grid place-items-center text-moss font-semibold shrink-0">
                      {c.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink truncate">{c.name}</div>
                      <div className="text-xs text-ink-3 truncate">{c.email}</div>
                    </div>
                    <ResetPasswordButton
                      userId={c.id}
                      userName={c.name}
                      userEmail={c.email}
                      trigger={
                        <Button size="sm" variant="ghost" className="rounded-lg h-10 mr-1" data-testid={`mobile-reset-pwd-coach-trigger-${c.id}`} aria-label={`Reset password for ${c.name}`} title="Reset password">
                          <Key size={16} className="text-ink-2" />
                        </Button>
                      }
                    />
                    <EditUserDialog
                      user={c}
                      onSaved={(u) => setCoaches((cur) => cur.map((x) => x.id === u.id ? u : x))}
                      trigger={
                        <Button size="sm" variant="ghost" className="rounded-lg h-10 mr-1" data-testid={`mobile-edit-coach-trigger-${c.id}`} aria-label={`Edit info for ${c.name}`} title="Edit info">
                          <PencilSimple size={16} className="text-ink-2" />
                        </Button>
                      }
                    />
                    <Button size="sm" variant="ghost" onClick={() => del(c.id, "coach")} className="rounded-lg h-10 text-error hover:bg-red-50 hover:text-error" data-testid={`mobile-delete-coach-${c.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block hs-card overflow-hidden">
              {filteredCoaches.length === 0 ? (
                <div className="p-10 text-center text-ink-3">{q ? `No coaches match "${query}".` : "No coaches yet."}</div>
              ) : (
                <table className="w-full font-data text-sm">
                  <thead className="bg-muted2 text-ink-2">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">Name</th>
                      <th className="text-left px-6 py-3 font-medium">Email</th>
                      <th className="text-right px-6 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoaches.map((c) => (
                      <tr key={c.id} className="border-t border-line" data-testid={`coach-row-${c.id}`}>
                        <td className="px-6 py-4 font-medium">{c.name}</td>
                        <td className="px-6 py-4 text-ink-2">{c.email}</td>
                        <td className="px-6 py-4 text-right space-x-1">
                          <ResetPasswordButton
                            userId={c.id}
                            userName={c.name}
                            userEmail={c.email}
                            trigger={
                              <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`reset-pwd-coach-trigger-${c.id}`} aria-label={`Reset password for ${c.name}`} title="Reset password">
                                <Key size={14} className="text-ink-2" />
                              </Button>
                            }
                          />
                          <EditUserDialog
                            user={c}
                            onSaved={(u) => setCoaches((cur) => cur.map((x) => x.id === u.id ? u : x))}
                            trigger={
                              <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`edit-coach-trigger-${c.id}`} aria-label={`Edit info for ${c.name}`} title="Edit info">
                                <PencilSimple size={14} className="text-ink-2" />
                              </Button>
                            }
                          />
                          <Button size="sm" variant="ghost" onClick={() => del(c.id, "coach")} className="rounded-lg text-error hover:bg-red-50 hover:text-error" data-testid={`delete-${c.id}`}>
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ─── Weekly recap send button (admin only) ──────────────────────────
// One-tap fire of the same job that runs every Saturday at 21:00 EST.
// Generates one email per athlete and ships them all to the configured
// admin inbox so they can be forwarded individually.
function WeeklyRecapButton({ athleteCount }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send weekly recap emails for all ${athleteCount} athletes now?\n\nEach athlete gets their own email — all delivered to the admin's recap inbox.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/weekly-recap/send-all");
      if (data.failed > 0) {
        toast.warning(`Sent ${data.sent} recap${data.sent !== 1 ? "s" : ""} · ${data.failed} failed`);
      } else {
        toast.success(`Sent ${data.sent} weekly recap${data.sent !== 1 ? "s" : ""} to ${data.recipient}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={send}
      disabled={busy || athleteCount === 0}
      variant="outline"
      className="rounded-xl h-10"
      data-testid="send-weekly-recap-button"
    >
      <EnvelopeSimple size={14} className="mr-1.5" weight="bold" />
      {busy ? "Sending…" : "Send weekly recap"}
    </Button>
  );
}
